import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import isNil from "lodash/isNil";
import { useDispatch, useSelector } from "react-redux";

import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeader from "../../components/layout/TitledHeader";

import { AddCircleOutline, EastRounded, OpenInNew } from "@mui/icons-material";
import { Box, Button, Card, CircularProgress } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";
import OverviewItem from "../../components/data/OverviewItem";
import SearchInput from "../../components/inputs/SearchInput";
import LoadingFallback from "../../components/LoadingFallback";
import ToggleNavigation from "../../components/navigation/ToggleNavigation";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import OpenInNewTabButton from "../../components/ui/buttons/OpenInNewTabButton";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useReduxHook } from "../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import { workspaceTabsAdd } from "../../redux/reducers/workspaceTabsReducer";
import { filterLoansByUserEmail } from "../../utils/loanPipeline/filterLoansByUserEmail";
import CreateLoanButton from "./buttons/CreateLoanButton";
import DownloadPipelineAsZipButton from "./buttons/DownloadPipelineAsZipButton";
import ScreenBodyWrapper from "./components/ScreenBodyWrapper";

const STATUS_TYPE_CONFIG = {
  Prospects: {
    label: "Prospects",
    // Adjust these as needed based on your actual data
    statuses: ["Prospect"],
  },
  "In Progress": {
    label: "In Progress",
    statuses: [
      "Terms Accepted",
      "Processing",
      "Appraisal Ordered",
      "Appraisal In",
      "Underwriting",
      "Final Review",
      "Clear To Close",
    ],
  },
  Closed: {
    label: "Closed",
    statuses: ["Closed", "Closed w/ Post Closing Conditions"],
  },
  "Dead/Inactive": {
    label: "Dead/Inactive",
    statuses: ["Balance Sheet", "Loan Sold", "Inactive", "Dead"],
  },
};

const LoanPipelineBoardScreen = () => {
  const ref = useRef();
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanPipelinePreFiltered = useSelector((state) => state?.loanPipeline);
  const { isDevUser, userEmail } = useReduxHook();

  const { loading, getUserPipeline } = usePipelineHook();
  const dispatch = useDispatch();
  const isFetchingRef = useRef(false);

  const statusTypeOptions = [
    "Prospects",
    "In Progress",
    "Closed",
    "Dead/Inactive",
  ];
  const [statusType, setStatusType] = useState(statusTypeOptions[1]);

  const [assignedToMe, setAssignedToMe] = useState(false);

  const loanPipeline = useMemo(() => {
    if (assignedToMe) {
      return filterLoansByUserEmail(loanPipelinePreFiltered, userEmail);
    }

    return loanPipelinePreFiltered;
  }, [loanPipelinePreFiltered, isDevUser, assignedToMe, userEmail]);

  const fetchLoanPipeline = useCallback(() => {
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    getUserPipeline({
      onSuccessFn: (data) => {
        dispatch(loanPipelineSet(data));
        isFetchingRef.current = false;
      },
      onErrorFn: (err) => {
        console.error("GetUserPipeline error:", err);
        isFetchingRef.current = false;
      },
    });
  }, [getUserPipeline, dispatch]);

  useEffect(() => {
    if (isNil(loanPipeline) && !isFetchingRef.current) {
      fetchLoanPipeline();
    }
    if (!isNil(loanDrilldown)) {
      clearLoanDrilldownData();
      // dispatch(loanDrilldownClear());
      // dispatch(sidebarReset());
    }
  }, [loanPipeline, fetchLoanPipeline, loanDrilldown, dispatch]);

  return (
    <Screen>
      <TitledHeader
        hideBottomBorder
        title={"My Pipeline"}
        titleEndContent={
          <Tooltip title="Refresh loan data">
            <IconButton
              size="small"
              onClick={() => dispatch(loanPipelineSet(null))}
            >
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        sx={{ pb: 1 }}
        endContent={
          <Flx gap={1} jb ac>
            <CreateLoanButton />
            <DownloadPipelineAsZipButton />
          </Flx>
        }
      />

      <Suspense fallback={<LoadingFallback />}>
        <ScreenBodyWrapper>
          {loading ? (
            <Flx column fw center>
              <Txt sx={{ mb: 2 }}>Loading your pipeline...</Txt>
              <CircularProgress />
            </Flx>
          ) : (
            <LoanPipelineBoard
              loans={loanPipeline}
              statusType={statusType}
              setStatusType={setStatusType}
            />
          )}
        </ScreenBodyWrapper>
      </Suspense>
    </Screen>
  );
};

const LoanPipelineBoard = ({ loans = [], statusType, setStatusType }) => {
  const [quickFilter, setQuickFilter] = useState("");

  // Group loans by statusType (Prospects, In Progress, Closed, Dead/Inactive)
  const loansByStatusType = useMemo(() => {
    const initial = {};
    Object.keys(STATUS_TYPE_CONFIG).forEach((type) => {
      initial[type] = [];
    });

    if (isNil(loans) || isEmpty(loans)) {
      return initial;
    }

    const result = { ...initial };

    loans.forEach((loan) => {
      const loanStatus = loan?.loanStatus;
      if (!loanStatus) return;

      Object.entries(STATUS_TYPE_CONFIG).forEach(([type, config]) => {
        if (config.statuses.includes(loanStatus)) {
          result[type].push(loan);
        }
      });
    });

    return result;
  }, [loans]);

  // Loans for the currently selected statusType
  const currentLoans = loansByStatusType[statusType] || [];

  // Columns depend on the selected statusType
  const statusList = useMemo(() => {
    const config = STATUS_TYPE_CONFIG[statusType];
    return config ? config.statuses : [];
  }, [statusType]);

  // Filter statusList to only include statuses that actually have loans
  const activeStatuses = statusList.filter((status) =>
    currentLoans.some((loan) => loan?.loanStatus === status)
  );

  // Build tabs with counts in the labels, e.g. "In Progress (53)"
  const tabs = useMemo(() => {
    return Object.keys(STATUS_TYPE_CONFIG).map((type) => {
      const count = loansByStatusType[type]?.length || 0;
      const baseLabel = STATUS_TYPE_CONFIG[type].label;
      const label = `${baseLabel} (${count})`;

      // Expecting ToggleNavigation to accept { label, value }
      return {
        label,
        value: type,
      };
    });
  }, [loansByStatusType]);

  if (activeStatuses.length === 0) {
    return (
      <Flx column fw>
        <Flx ac gap={2} fw jb sx={{ px: 2 }}>
          <ToggleNavigation
            className="pipeline-selector"
            tabs={tabs}
            value={statusType}
            variant={"underline"}
            onChange={setStatusType}
          />
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder={"Search in table..."}
            sx={{
              width: "320px",
              ".MuiInputBase-root": {
                borderRadius: "50px !important",
                background: "#ffffff",
                fontSize: "11px",
                pl: 1.2,
                pr: 0.5,
                ".MuiSvgIcon-root": {
                  fontSize: "11px",
                },
              },
              input: {
                py: 1,
                height: "unset",
                borderRadius: "50px",
                fontSize: "11px",
              },
            }}
          />
        </Flx>
        <Txt secondary sx={{ px: 2, pt: 1 }}>
          No loans in pipeline for this status.
        </Txt>
      </Flx>
    );
  }

  return (
    <Flx column fw gap={0.5}>
      <Flx ac gap={2} fw jb sx={{ pr: 2, pl: 1 }}>
        <ToggleNavigation
          className="pipeline-selector"
          tabs={tabs}
          value={statusType}
          variant={"underline"}
          onChange={setStatusType}
        />

        <SearchInput
          value={quickFilter}
          onChange={setQuickFilter}
          placeholder={"Search in table..."}
          sx={{
            width: "320px",
            ".MuiInputBase-root": {
              borderRadius: "50px !important",
              background: "#ffffff",
              fontSize: "11px",
              pl: 1.2,
              pr: 0.5,
              ".MuiSvgIcon-root": {
                fontSize: "11px",
              },
            },
            input: {
              py: 1,
              height: "unset",
              borderRadius: "50px",
              fontSize: "11px",
            },
          }}
        />
      </Flx>

      <Flx fw sx={{ overflowX: "auto", height: "100%" }}>
        {activeStatuses.map((status, i) => (
          <LoanStatusBoardColumn
            key={status}
            loans={currentLoans}
            status={status}
            position={
              i === 0
                ? "first"
                : i === activeStatuses.length - 1
                  ? "last"
                  : "middle"
            }
          />
        ))}
      </Flx>
    </Flx>
  );
};

const LoanStatusBoardColumn = ({ status, loans = [], position }) => {
  const dispatch = useDispatch();

  const filteredLoans =
    loans?.filter((loan) => loan?.loanStatus === status) || [];

  const overviewItemStyles = useMemo(() => {
    return {
      justifyContent: "space-between",
      py: 1,
      borderBottom: "2px dashed #efefef",
      ".txt": {
        fontSize: "10px",
      },
    };
  }, []);

  return (
    <BoardColumn label={status} position={position}>
      {filteredLoans?.map((loan) => (
        <BoardCard
          key={loan._id}
          loan={loan}
          label={
            <Txt sx={{ fontWeight: 500, fontSize: "11px", lineHeight: "20px" }}>
              <Txt
                className="loan-number"
                sx={{
                  px: 0.6,
                  py: 0.2,
                  borderRadius: "40px",
                  flexShrink: 0,
                  fontSize: "10px",
                  mr: 0.8,
                  lineHeight: "20px",
                  backgroundColor: "#062e6f12",
                }}
              >
                #{loan?.loanNumber}
              </Txt>
              {loan?.loanName || "Untitled Loan"}
            </Txt>
          }
        >
          <OverviewItem
            sx={overviewItemStyles}
            stacked={false}
            label="Loan #: "
            value={loan.loanNumber}
          />

          <OverviewItem
            stacked={false}
            sx={overviewItemStyles}
            label="Product Type:"
            value={loan.loanProductType}
          />
          <OverviewItem
            stacked={false}
            sx={{ ...overviewItemStyles, borderBottom: "none" }}
            label="Product Purpose:"
            value={loan.loanPurpose}
          />
        </BoardCard>
      ))}
    </BoardColumn>
  );
};

const BoardColumn = ({ label, children, position }) => {
  const styles = useMemo(() => {
    if (position === "first") {
      return {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        // borderBottomLeftRadius: 0,
      };
    }
    if (position === "last") {
      return {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        // borderBottomRightRadius: 0,

        borderLeft: "none",
      };
    }
    return {
      borderLeft: "none",
      borderRadius: 0,
    };
  }, [position]);
  return (
    <Flx
      column
      // gap={3}
      sx={{
        // border: `1px solid ${grey[200]}`,
        border: "1px solid #e0e0e0",
        background: grey[50],
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        flexGrow: 1,
        minWidth: "250px",
        ...styles,
      }}
    >
      <Flx sx={{ flexGrow: 0, p: 2 }}>
        <Htag variant="h3" bold>
          {label}
        </Htag>
      </Flx>
      <Flx sx={{ flexGrow: 1, overflowY: "auto", py: 1, px: 2 }} column gap={2}>
        {isNil(children) || isEmpty(children) ? (
          <Txt secondary>No items</Txt>
        ) : (
          children
        )}
      </Flx>
    </Flx>
  );
};

const BoardCard = ({ label, children, loan }) => {
  const [collapsed, setCollapsed] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onAddToTabs = (d) => {
    const data = {
      loanNumber: d.loanNumber,
      loanName: d.loanName,
    };
    dispatch(workspaceTabsAdd(data));
  };
  return (
    <Card sx={{ p: 0, flexShrink: 0 }}>
      <Box
        onClick={() => setCollapsed(!collapsed)}
        sx={{
          gap: 1,
          display: "flex",
          alignItems: "center",
          p: 1.2,
          cursor: "pointer",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        {label}
      </Box>
      <Flx jb fw sx={{ mt: 0, px: 1, py: 0.6 }} g={2}>
        <Flx ac>
          <Tooltip title="Open Loan In New Tab">
            <OpenInNewTabButton
              onClick={() => navigate(`/loan/${loan?.loanNumber}`)}
              size="small"
            >
              <OpenInNew
                className="thin"
                sx={{ fontSize: "16px !important" }}
              />
            </OpenInNewTabButton>
          </Tooltip>
          <Tooltip title="Add Loan To List">
            <IconButton
              color="primary"
              onClick={() => onAddToTabs(loan)}
              size="small"
            >
              <AddCircleOutline
                className="thin"
                sx={{ fontSize: "16px !important" }}
              />
            </IconButton>
          </Tooltip>
        </Flx>
        <Flx ac>
          <Button
            size="small"
            onClick={() => setCollapsed(!collapsed)}
            variant={collapsed ? "text" : "outlined"}
            sx={{
              color: grey[600],
              fontWeight: 400,
              px: 0.5,
              // borderRadius: "50px",
              // minHeight: "10px",
              // height: "20px",
              // lineHeight: 1,
              fontSize: "10px",
            }}
            color="inherit"
            // endIcon={
            //   <EastRounded
            //     className="thin"
            //     sx={{ ml: -0.5, fontSize: "16px !important" }}
            //   />
            // }
          >
            {collapsed ? "Show" : "Hide"} Details
          </Button>
          <Button
            size="small"
            onClick={() => navigate(`/loan/${loan?.loanNumber}`)}
            variant="text"
            sx={{ fontSize: "10px" }}
            endIcon={
              <EastRounded
                className="thin"
                sx={{ ml: -0.5, fontSize: "16px !important" }}
              />
            }
          >
            View
          </Button>
        </Flx>
      </Flx>
      {/* <Box
        onClick={() => setCollapsed(!collapsed)}
        sx={{
          gap: 1,
          display: "flex",
          alignItems: "center",
          // p: 2,
          cursor: "pointer",
          width: "100%",
          justifyContent: "center",
          borderTop: collapsed ? "#ffffff" : "1.5px solid #efefef",
          // background: collapsed ? "#ffffff" : grey[50],
        }}
      >
        {collapsed ? (
          <KeyboardArrowDown className="thin-8" />
        ) : (
          <KeyboardArrowUp className="thin-8" />
        )}
      </Box> */}
      {collapsed ? null : (
        <Box sx={{ p: 1, width: "100%", borderTop: "1.5px solid #eee" }}>
          {children}
        </Box>
      )}
    </Card>
  );
};

export default LoanPipelineBoardScreen;
